import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  TablePagination,
  Chip,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterListIcon from '@mui/icons-material/FilterList';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GavelIcon from '@mui/icons-material/Gavel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reservationService } from '../../services/api';
import type { Reservation } from '../../types';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=1920&q=80';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Toutes' },
  { value: 'pending', label: 'En attente' },
  { value: 'confirmed', label: 'Confirmée' },
  { value: 'completed', label: 'Terminée' },
  { value: 'cancelled', label: 'Annulée' },
];

const STATUS_CHIP: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  pending: { label: 'En attente', color: 'warning' },
  confirmed: { label: 'Confirmée', color: 'info' },
  completed: { label: 'Terminée', color: 'success' },
  cancelled: { label: 'Annulée', color: 'error' },
};

const ReservationsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const subClientIdFilter = searchParams.get('sub_client_id');
  const [list, setList] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const [dateDepartFrom, setDateDepartFrom] = useState('');
  const [dateDepartTo, setDateDepartTo] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [total, setTotal] = useState(0);

  const [quickViewReservation, setQuickViewReservation] = useState<any>(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewLoading, setQuickViewLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        per_page: perPage,
      };
      if (search.trim()) params.search = search.trim();
      // This checkbox is meant to *include* cancelled reservations in addition
      // to any other status filters already selected.
      params.include_cancelled = showCancelled;
      if (selectedStatuses.length > 0) {
        params.status = selectedStatuses.join(',');
      }
      if (dateDepartFrom) params.date_depart_from = dateDepartFrom;
      if (dateDepartTo) params.date_depart_to = dateDepartTo;
      if (subClientIdFilter && /^\d+$/.test(subClientIdFilter)) {
        params.sub_client_id = Number(subClientIdFilter);
      }
      const res = await reservationService.getList(params as any);
      const data = res.data as any;
      setList(Array.isArray(data.data) ? data.data : []);
      setTotal(data.total ?? data.data?.length ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des réservations.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, selectedStatuses, showCancelled, dateDepartFrom, dateDepartTo, subClientIdFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setPage(0);
    fetchList();
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm('Annuler cette réservation ?')) return;
    try {
      await reservationService.cancel(id);
      fetchList();
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de l'annulation.");
    }
  };

  const handleExport = async () => {
    try {
      const params: Record<string, string> = {};
      if (search.trim()) params.search = search.trim();
      if (selectedStatuses.length > 0) params.status = selectedStatuses.join(',');
      // Keep export consistent with the list filters.
      if (showCancelled) params.include_cancelled = String(showCancelled);
      if (dateDepartFrom) params.date_depart_from = dateDepartFrom;
      if (dateDepartTo) params.date_depart_to = dateDepartTo;
      if (subClientIdFilter && /^\d+$/.test(subClientIdFilter)) {
        params.sub_client_id = subClientIdFilter;
      }
      const res = await reservationService.export(params);
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reservations-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de l'export.");
    }
  };

  const handleQuickView = async (id: number) => {
    setQuickViewLoading(true);
    setQuickViewOpen(true);
    try {
      const res = await reservationService.getById(id);
      setQuickViewReservation((res.data as any).data ?? res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement de la réservation.');
      setQuickViewOpen(false);
    } finally {
      setQuickViewLoading(false);
    }
  };

  const handleStatusToggle = (value: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value],
    );
    setPage(0);
  };

  const formatDateTime = (d: string | undefined) => {
    if (!d) return '–';
    try {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return d;
    }
  };

  const formatAmount = (n: number | undefined) => {
    if (n == null) return '–';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n));
  };

  const qv = quickViewReservation;
  const qvStatus = qv ? STATUS_CHIP[qv.status] || { label: qv.status, color: 'default' as const } : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      {/* Hero Header */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 100, md: 120 },
          backgroundImage: `linear-gradient(135deg, rgba(44,62,80,0.92) 0%, rgba(52,73,94,0.88) 100%), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
          Réservations
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Gérez l'ensemble de vos réservations
        </Typography>
      </Box>

      {/* Search & Filters */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e8ecf1', py: 2.5, px: 2 }}>
        <Box sx={{ mx: 'auto' }}>
          {subClientIdFilter && /^\d+$/.test(subClientIdFilter) && (
            <Chip
              label={`Filtre client (sous-client N° ${subClientIdFilter})`}
              onDelete={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('sub_client_id');
                setSearchParams(next, { replace: true });
                setPage(0);
              }}
              color="primary"
              variant="outlined"
              sx={{ mb: 1.5 }}
            />
          )}
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher par client, immatriculation, catégorie, agence, numéro..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#fafbfc',
                '&:hover': { bgcolor: '#f5f6f8' },
              },
            }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <FilterListIcon sx={{ fontSize: 16, color: '#2c3e50' }} />
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Statut
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0 }}>
                {STATUS_OPTIONS.filter((opt) => opt.value !== 'all' && opt.value !== 'cancelled').map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedStatuses.includes(opt.value)}
                        onChange={() => handleStatusToggle(opt.value)}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showCancelled}
                      onChange={(e) => { setShowCancelled(e.target.checked); setPage(0); }}
                    />
                  }
                  label={<Typography variant="body2">Afficher les annulées</Typography>}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 1.5 }}>
                <TextField
                  size="small"
                  label="Du"
                  type="date"
                  value={dateDepartFrom}
                  onChange={(e) => setDateDepartFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
                  }}
                  sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }}
                />
                <TextField
                  size="small"
                  label="Au"
                  type="date"
                  value={dateDepartTo}
                  onChange={(e) => setDateDepartTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
                  }}
                  sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }}
                />
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    bgcolor: '#2c3e50',
                    '&:hover': { bgcolor: '#34495e' },
                  }}
                >
                  Rechercher
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-end', pt: 0.5 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/reservations/create')}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  px: 3.5,
                  py: 1.2,
                  bgcolor: '#27ae60',
                  '&:hover': { bgcolor: '#219a52' },
                  boxShadow: '0 4px 14px rgba(39,174,96,0.3)',
                  minWidth: 220,
                }}
              >
                Nouvelle réservation
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#2c3e50', color: '#2c3e50', minWidth: 220 }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, py: 3 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e0e4ea',
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#2c3e50' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700, py: 1.5, whiteSpace: 'nowrap' }}>Numéro</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Type véhicule</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Cat. véhicule</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Immat.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>N° Client</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Intitulé Client</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Agence Départ</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Date Départ</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Agence Retour</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Date Retour</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Statut</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>Montant</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                      <CircularProgress sx={{ color: '#2c3e50' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Chargement...</Typography>
                    </TableCell>
                  </TableRow>
                ) : list.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                      <EventAvailableIcon sx={{ fontSize: 48, color: '#cfd8dc', mb: 1 }} />
                      <Typography variant="body1" fontWeight={600} color="text.secondary">Aucune réservation trouvée</Typography>
                      <Typography variant="body2" color="text.secondary">Ajustez vos filtres ou créez une nouvelle réservation.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  list.map((r, idx) => {
                    const statusInfo = STATUS_CHIP[r.status] || { label: r.status, color: 'default' as const };
                    const vehicleType = (r.vehicle as any)?.vehicle_category?.type?.name || '';
                    const vehicleCategory = r.vehicle?.vehicle_category?.name || '';
                    return (
                      <TableRow
                        key={r.id}
                        hover
                        sx={{
                          bgcolor: idx % 2 === 0 ? 'white' : '#fafbfd',
                          '&:hover': { bgcolor: '#f0f4f8' },
                          cursor: 'pointer',
                          transition: 'background-color 0.15s',
                        }}
                        onClick={() => navigate(`/reservations/${r.id}`)}
                      >
                        <TableCell sx={{ fontWeight: 700, color: '#2c3e50', whiteSpace: 'nowrap' }}>{r.reference || r.id}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{vehicleType || '–'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{vehicleCategory || '–'}</TableCell>
                        <TableCell>
                          <Chip label={r.vehicle?.registration_number || '–'} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#546e7a', whiteSpace: 'nowrap' }}>{r.sub_client?.id || r.client_id || '–'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {r.customer_first_name} {r.customer_last_name}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.pickup_agency?.name || '–'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.pickup_datetime)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.return_agency?.name || '–'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.return_datetime)}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusInfo.label}
                            color={statusInfo.color}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatAmount(r.total_amount ?? r.total_cost)}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Aperçu rapide">
                              <IconButton size="small" onClick={() => handleQuickView(r.id)} sx={{ color: '#2c3e50' }}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Modifier">
                              <IconButton size="small" onClick={() => navigate(`/reservations/${r.id}/edit`)} sx={{ color: '#2c3e50' }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {r.status !== 'cancelled' && (
                              <Tooltip title="Procéder au contrat">
                                <IconButton size="small" onClick={() => navigate(`/reservations/${r.id}/contract`)} sx={{ color: '#8e44ad' }}>
                                  <GavelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {r.status !== 'cancelled' && (
                              <Tooltip title="Annuler">
                                <IconButton size="small" onClick={() => handleCancel(r.id)} sx={{ color: '#e74c3c' }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage="Lignes par page"
              sx={{ borderTop: '1px solid #e0e4ea' }}
            />
          )}
        </Paper>
      </Box>

      {/* Quick View Dialog */}
      <Dialog open={quickViewOpen} onClose={() => setQuickViewOpen(false)} fullWidth maxWidth="md">
        <DialogContent sx={{ p: 0 }}>
          {quickViewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#2c3e50' }} />
            </Box>
          ) : qv ? (
            <Box sx={{ p: 3 }}>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5" fontWeight={700} color="#2c3e50">
                    {qv.reference || `#${qv.id}`}
                  </Typography>
                  {qvStatus && (
                    <Chip label={qvStatus.label} color={qvStatus.color} size="small" sx={{ fontWeight: 600 }} />
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Using Box instead of Grid item props to avoid MUI Grid type mismatch */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Vehicle Info */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#2c3e50" gutterBottom>
                    Véhicule
                  </Typography>
                  <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                    <Typography variant="body2">
                      <strong>Immatriculation :</strong> {qv.vehicle?.registration_number || '–'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Marque / Modèle :</strong> {qv.vehicle?.marque || ''} {qv.vehicle?.modele || ''}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Catégorie :</strong> {qv.vehicle?.vehicle_category?.name || '–'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type :</strong> {(qv.vehicle as any)?.vehicle_category?.type?.name || qv.vehicle?.vehicle_type?.name || '–'}
                    </Typography>
                  </Box>
                </Box>

                {/* Client Info */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#2c3e50" gutterBottom>
                    Client
                  </Typography>
                  <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                    <Typography variant="body2">
                      <strong>Nom :</strong> {qv.customer_civility || ''} {qv.customer_first_name || ''} {qv.customer_last_name || ''}
                    </Typography>
                    {qv.customer_company && (
                      <Typography variant="body2"><strong>Société :</strong> {qv.customer_company}</Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Email :</strong> {qv.customer_email || '–'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Téléphone :</strong> {qv.customer_phone || '–'}
                    </Typography>
                    {qv.customer_address && (
                      <Typography variant="body2">
                        <strong>Adresse :</strong> {qv.customer_address}{qv.customer_postal_code ? `, ${qv.customer_postal_code}` : ''}{qv.customer_city ? ` ${qv.customer_city}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Dates & Agencies */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#2c3e50" gutterBottom>
                    Dates &amp; Agences
                  </Typography>
                  <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                    <Typography variant="body2">
                      <strong>Départ :</strong> {formatDateTime(qv.pickup_datetime)} — {qv.pickup_agency?.name || '–'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Retour :</strong> {formatDateTime(qv.return_datetime)} — {qv.return_agency?.name || '–'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Durée :</strong> {qv.rental_days != null ? `${qv.rental_days} jour(s)` : '–'}
                    </Typography>
                  </Box>
                </Box>

                {/* Amount Breakdown */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#2c3e50" gutterBottom>
                    Montants
                  </Typography>
                  <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                    {qv.pack_name && (
                      <Typography variant="body2"><strong>Pack :</strong> {qv.pack_name}</Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Tarif journalier :</strong> {formatAmount(qv.daily_rate)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Location de base :</strong> {formatAmount(qv.base_rental_amount)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Prestations :</strong> {formatAmount(qv.prestations_amount)}
                    </Typography>
                    {(qv.discount_amount != null && qv.discount_amount > 0) && (
                      <Typography variant="body2" color="success.main">
                        <strong>Remise :</strong> -{formatAmount(qv.discount_amount)}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Sous-total :</strong> {formatAmount(qv.subtotal)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Franchise :</strong> {formatAmount(qv.franchise_amount)}
                    </Typography>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="body1" fontWeight={700} color="#2c3e50">
                      Total : {formatAmount(qv.total_amount ?? qv.total_cost)}
                    </Typography>
                  </Box>
                </Box>

                {/* Prestations table */}
                {qv.prestations && qv.prestations.length > 0 && (
                  <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#2c3e50" gutterBottom>
                      Prestations
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f0f4f8' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Prestation</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Tarif unit.</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Qté</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(qv.prestations as any[]).map((p: any, i: number) => (
                            <TableRow key={i}>
                              <TableCell>{p.name || p.prestation?.name || '–'}</TableCell>
                              <TableCell>{p.pricing_mode === 'daily' ? 'Journalier' : 'Forfait'}</TableCell>
                              <TableCell align="right">{formatAmount(p.unit_rate)}</TableCell>
                              <TableCell align="right">{p.quantity ?? 1}</TableCell>
                              <TableCell align="right">{formatAmount(p.total_amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Payments summary */}
                {qv.payments && qv.payments.length > 0 && (
                  <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#2c3e50" gutterBottom>
                      Paiements
                    </Typography>
                    <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                      {(qv.payments as any[]).map((pay: any, i: number) => (
                        <Typography key={i} variant="body2">
                          {pay.method || pay.payment_method || 'Paiement'} — {formatAmount(pay.amount)} — {formatDateTime(pay.paid_at || pay.created_at)}
                        </Typography>
                      ))}
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="body2" fontWeight={700}>
                        Total payé : {formatAmount((qv.payments as any[]).reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0))}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setQuickViewOpen(false)} sx={{ textTransform: 'none' }}>
            Fermer
          </Button>
          {qv && (
            <Button
              variant="contained"
              onClick={() => { setQuickViewOpen(false); navigate(`/reservations/${qv.id}/edit`); }}
              sx={{ textTransform: 'none', bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}
            >
              Modifier
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReservationsList;
